'use client'

// Question-bank explorer UI. Public counts; launching a session mirrors the
// practice-hub rules — topic practice is FREE for every signed-in user
// (packaging v2), so the only gate here is authentication.

import { useMemo, useState } from 'react'
import Link from 'next/link'
import { ChevronDown } from 'lucide-react'
import type { ExplorerTree, ExplorerTopic } from '@/lib/practice/bank-explorer'

type Access = 'member' | 'anonymous'

const SECTION_LABEL: Record<'RW' | 'MATH', string> = {
  RW: 'Reading & Writing',
  MATH: 'Math',
}

function practiceHref(topic: string, access: Access): string {
  if (access === 'member') return `/practice/session?topic=${encodeURIComponent(topic)}&count=all`
  return `/signin?callbackUrl=${encodeURIComponent('/practice/browse')}`
}

function DifficultyChips({ topic }: { topic: ExplorerTopic }) {
  const chips: Array<{ key: 'E' | 'M' | 'H'; label: string; cls: string }> = [
    { key: 'E', label: 'Easy', cls: 'text-emerald-700 dark:text-emerald-300' },
    { key: 'M', label: 'Medium', cls: 'text-amber-700 dark:text-amber-300' },
    { key: 'H', label: 'Hard', cls: 'text-rose-700 dark:text-rose-300' },
  ]
  return (
    <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      {chips.map(
        (c) =>
          topic.byDifficulty[c.key] > 0 && (
            <span key={c.key} className={c.cls}>
              {c.label} {topic.byDifficulty[c.key]}
            </span>
          ),
      )}
    </span>
  )
}

export default function BankExplorerClient({
  tree,
  access,
}: {
  tree: ExplorerTree
  access: Access
}) {
  const [section, setSection] = useState<'RW' | 'MATH'>('RW')
  const [openDomain, setOpenDomain] = useState<string | null>(null)

  const active = useMemo(
    () => tree.sections.find((s) => s.section === section) ?? null,
    [tree, section],
  )

  return (
    <div>
      {/* Section tabs */}
      <div
        className="mb-6 inline-flex rounded-full border border-border bg-card p-1 text-sm"
        role="tablist"
        aria-label="Section"
      >
        {tree.sections.map((s) => (
          <button
            key={s.section}
            role="tab"
            aria-selected={section === s.section}
            onClick={() => {
              setSection(s.section)
              setOpenDomain(null)
            }}
            className={`rounded-full px-4 py-1.5 font-medium transition ${
              section === s.section
                ? 'bg-brand text-on-brand'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {SECTION_LABEL[s.section]}{' '}
            <span className={section === s.section ? 'text-on-brand/80' : ''}>({s.total})</span>
          </button>
        ))}
      </div>

      {/* Domain accordions */}
      <div className="space-y-3" data-testid="explorer-domains">
        {active?.domains.map((d) => {
          const isOpen = openDomain === d.domain
          return (
            <div
              key={d.domain}
              className="overflow-hidden rounded-xl border border-border bg-card/60"
            >
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpenDomain(isOpen ? null : d.domain)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left hover:bg-card/80"
              >
                <span className="text-sm font-semibold md:text-base">{d.domain}</span>
                <span className="flex items-center gap-3 text-xs text-muted-foreground">
                  {d.total} questions
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </span>
              </button>
              {isOpen && (
                <ul className="divide-y divide-border border-t border-border">
                  {d.topics.map((t) => (
                    <li
                      key={t.topic}
                      className="flex flex-wrap items-center justify-between gap-3 px-5 py-3"
                      data-testid="explorer-topic"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground">{t.topic}</div>
                        <div className="mt-0.5 flex items-center gap-3">
                          <span className="text-xs text-muted-foreground">{t.total} questions</span>
                          <DifficultyChips topic={t} />
                        </div>
                      </div>
                      <Link
                        href={practiceHref(t.topic, access)}
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent"
                        data-testid="explorer-practice-link"
                      >
                        {access === 'member' ? 'Practice' : 'Sign in to practice'}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
